import * as z from "zod";

export const role_enum = z.enum(["superuser", "school_admin", "substitute"], {
  error: "Please select a valid Role",
});

export const timeString = (label: string) =>
  z
    .string({ error: `${label} is required` })
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, `${label} is not a valid time`);

type FormFieldError = { name: string; message: string };

export type ParseResult<T> =
  | { ok: true; data: T }
  | { ok: false; field_errors: FormFieldError[]; error?: string; status?: number };

function formDataToObject(
  formData: FormData
): Record<string, FormDataEntryValue | FormDataEntryValue[]> {
  const result: Record<string, FormDataEntryValue | FormDataEntryValue[]> = {};
  for (const key of new Set(formData.keys())) {
    const values = formData.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }
  return result;
}

// GGSelect emits `<input name="<field>" value="">` for empty multi-selects so
// the field is always present in FormData; the preprocessor collapses the
// empty string to `[]`.
export const array_field = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => {
      if (v === undefined) return undefined;
      if (v === "") return [];
      return Array.isArray(v) ? v : [v];
    },
    z.array(item)
  );

// Counterpart for nullable scalar fields: empty input becomes `null` so PUT
// payloads always carry the field.
export const nullable_field = <T extends z.ZodTypeAny>(inner: T) =>
  z.preprocess((v) => (v === "" || v == null ? null : v), inner.nullable());

// Plain text input where users type a comma-separated list (e.g. emails).
// Splits on commas, trims whitespace, drops empties, then validates each
// entry with `item`. Blank input collapses to `[]`.
export const comma_separated_field = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => {
    if (v === undefined || v === null || v === "") return [];
    if (Array.isArray(v)) return v;
    if (typeof v !== "string") return v;
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }, z.array(item));

export const checkbox_field = z.preprocess(
  (v) => {
    if (typeof v === "boolean") return v;
    if (v === undefined || v === null || v === "") return false;
    if (typeof v === "string") return v !== "false" && v !== "0";
    return Boolean(v);
  },
  z.boolean()
);

// Checkbox that is omitted from the payload when absent. Pair the checkbox
// with a hidden `<input type="hidden" name="{field}" value="false">` directly
// before it so an unchecked box still submits a "false" (otherwise unchecked
// boxes are absent from FormData and we can't distinguish "off" from "not on
// this form"). When both are submitted, the last value (the checkbox) wins.
export const optional_checkbox_field = z.preprocess(
  (v) => {
    if (v === undefined) return undefined;
    if (Array.isArray(v)) v = v[v.length - 1];
    if (typeof v === "boolean") return v;
    if (v === null || v === "") return undefined;
    if (typeof v === "string") return v !== "false" && v !== "0";
    return Boolean(v);
  },
  z.boolean().optional()
);

const NOTIFICATION_EVENTS = [
  "daily_digest",
  "shift_reminder",
  "job_claimed",
  "job_cancelled",
  "claim_confirmation",
] as const;

const NOTIFICATION_CHANNELS = ["email", "sms"] as const;

type NotificationField =
  `${(typeof NOTIFICATION_EVENTS)[number]}_${(typeof NOTIFICATION_CHANNELS)[number]}`;

export const notification_fields_shape = Object.fromEntries(
  NOTIFICATION_EVENTS.flatMap((event) =>
    NOTIFICATION_CHANNELS.map((channel) =>
      [`${event}_${channel}`, optional_checkbox_field] as const
    )
  )
) as Record<NotificationField, typeof optional_checkbox_field>;

// Empty `<input>` values submit as "" rather than being absent. Coerce
// blanks to undefined so we can distinguish "missing" from "malformed".
const blank_to_undefined = (v: unknown) =>
  v === "" || v == null ? undefined : v;

export const required_date_field = (label: string) =>
  z.preprocess(
    blank_to_undefined,
    z.iso.date({
      error: (issue) =>
        issue.input === undefined
          ? `${label} is required`
          : `${label} is not a valid date`,
    })
  );

export const optional_date_field = (label: string) =>
  z.preprocess(
    blank_to_undefined,
    z.iso.date(`${label} is not a valid date`).optional()
  );

// Wraps `sb.functions.invoke` so backend errors surface as readable messages.
// supabase-js exposes only "Edge Function returned a non-2xx status code" on
// `error.message`; the real body sits on `error.context` (a Response). We
// read it once and, for ZodError payloads from `zValidator`, map each issue
// to a `field_errors` entry so the right input lights up.
type HttpMethod = "POST" | "GET" | "PUT" | "PATCH" | "DELETE";
type InvokeBody = Record<string, unknown>;

type SupabaseInvoker = {
  functions: {
    invoke: (
      path: string,
      opts?: { method?: HttpMethod; body?: InvokeBody }
    ) => Promise<{ data: unknown; error: unknown }>;
  };
};

type ZodIssueLike = { path?: Array<string | number>; message: string };

function isResponseLike(v: unknown): v is { json: () => Promise<unknown> } {
  return (
    !!v &&
    typeof v === "object" &&
    "json" in v &&
    typeof (v as { json: unknown }).json === "function"
  );
}

function extractIssues(body: unknown): ZodIssueLike[] | null {
  if (!body || typeof body !== "object") return null;
  const direct = (body as { issues?: unknown }).issues;
  if (Array.isArray(direct)) return direct as ZodIssueLike[];

  const err = (body as { error?: unknown }).error;
  if (
    err &&
    typeof err === "object" &&
    (err as { name?: unknown }).name === "ZodError"
  ) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string") {
      try {
        const decoded: unknown = JSON.parse(msg);
        if (Array.isArray(decoded)) return decoded as ZodIssueLike[];
      } catch {
        return null;
      }
    }
  }
  return null;
}

export async function invokeApi<T = unknown>(
  sb: SupabaseInvoker,
  path: string,
  opts?: { method?: HttpMethod; body?: InvokeBody }
): Promise<ParseResult<T>> {
  const { data, error } = await sb.functions.invoke(path, opts);
  if (!error) return { ok: true, data: data as T };

  const ctx = (error as { context?: unknown }).context;
  const status =
    ctx && typeof ctx === "object" && "status" in ctx
      ? (ctx as { status?: number }).status
      : undefined;
  const fallback =
    (error as { message?: string }).message ?? "Request failed";
  const statusPart = status !== undefined ? { status } : {};

  if (!isResponseLike(ctx)) {
    return { ok: false, field_errors: [], error: fallback, ...statusPart };
  }

  let body: unknown;
  try {
    body = await ctx.json();
  } catch {
    return { ok: false, field_errors: [], error: fallback, ...statusPart };
  }

  const issues = extractIssues(body);
  if (issues && issues.length > 0) {
    const field_errors: FormFieldError[] = [];
    let formError: string | undefined;
    for (const issue of issues) {
      const key = issue.path?.[0];
      if (typeof key === "string" && key.length > 0) {
        field_errors.push({ name: key, message: issue.message });
      } else {
        formError ??= issue.message;
      }
    }
    return {
      ok: false,
      field_errors,
      ...(formError ? { error: formError } : {}),
      ...statusPart,
    };
  }

  const bodyError = (body as { error?: unknown } | null)?.error;
  if (typeof bodyError === "string") {
    return {
      ok: false,
      field_errors: [],
      error: bodyError,
      ...statusPart,
    };
  }

  return { ok: false, field_errors: [], error: fallback, ...statusPart };
}

export function parse<S extends z.ZodTypeAny>(
  schema: S,
  formData: FormData
): ParseResult<z.output<S>> {
  const input = formDataToObject(formData);
  const result = schema.safeParse(input);
  if (result.success) return { ok: true, data: result.data };

  const flat = z.flattenError(result.error);
  const field_errors: FormFieldError[] = [];
  for (const [name, messages] of Object.entries(flat.fieldErrors)) {
    for (const message of (messages as string[] | undefined) ?? []) {
      field_errors.push({ name, message });
    }
  }
  const formError = flat.formErrors?.[0];
  return {
    ok: false,
    field_errors,
    ...(formError ? { error: formError } : {}),
  };
}
